import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Collapse, DatePicker, Drawer, Form, Input, InputNumber, message, Modal, Popconfirm,
  Select, Space, Switch, Table, Tabs, Tag, Typography,
} from 'antd';
import {
  DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import useAuthStore from '../../store/authStore';
import api from '../../api/client';
import {
  APPENDIX_KIND_LABELS,
  CONTRACTOR_DOC_TYPE_LABELS, ContractorDocType,
  EcologyContractor, EcologyContractorAppendix, EcologyContractorDocument,
  EcologyObligation, LEGAL_ENTITY_LABELS,
  createContractor, createContractorDocument, createContractorObligation, deleteContractor,
  deleteContractorDocument, deleteObligation, downloadMonthlyReport, downloadTransferAct,
  listContractorAppendices, listContractorDocuments, listContractorObligations, listContractors,
  updateContractor, updateContractorDocument, updateObligation,
} from '../../api/ecology';

const { Text } = Typography;
const { TabPane } = Tabs;

// Сверка ФККО с выпиской из реестра лицензий № 194987 (Ситиматик) — перенесено из EcologyPage
const SITIMATIC_FKKO = [
  { fkko_code: '33579211204', name: 'Отходы разнородных пластмасс в смеси', found: true },
  { fkko_code: '41310001313', name: 'Отходы синтетических и полусинтетических масел моторных', found: true },
  { fkko_code: '43811301514', name: 'Тара полиэтиленовая, загрязнённая нефтепродуктами (содержание менее 15%)', found: true },
  { fkko_code: '44310301613', name: 'Фильтры окрасочных камер стекловолоконные отработанные, загрязнённые лакокрасочными материалами', found: true },
  { fkko_code: '46811202514', name: 'Тара из чёрных металлов, загрязнённая лакокрасочными материалами (содержание менее 5%)', found: true },
  { fkko_code: '48241501524', name: 'Светодиодные лампы, утратившие потребительские свойства', found: true },
  { fkko_code: '72310202394', name: 'Осадок механической очистки нефтесодержащих сточных вод, содержащий нефтепродукты в количестве менее 15%', found: true },
  { fkko_code: '73310001724', name: 'Мусор от офисных и бытовых помещений организаций несортированный (исключая крупногабаритный)', found: true },
  { fkko_code: '73321001724', name: 'Мусор и смет производственных помещений малоопасный', found: true },
  { fkko_code: '73339001714', name: 'Смет с территории предприятия малоопасный', found: true },
  { fkko_code: '74721101404', name: 'Твёрдые остатки от сжигания нефтесодержащих отходов', found: true },
  { fkko_code: '91920401603', name: 'Обтирочный материал, загрязнённый нефтью или нефтепродуктами (содержание нефти или нефтепродуктов 15% и более)', found: true },
  { fkko_code: '92130101524', name: 'Фильтры воздушные автотранспортных средств отработанные', found: true },
  { fkko_code: '92130201523', name: 'Фильтры очистки масла автотранспортных средств отработанные', found: true },
  { fkko_code: '92130301523', name: 'Фильтры очистки топлива автотранспортных средств отработанные', found: true },
  { fkko_code: '46101001205', name: 'Лом и отходы, содержащие незагрязнённые чёрные металлы в виде изделий, кусков, несортированные', found: false },
  { fkko_code: '92121001313', name: 'Отходы антифризов на основе этиленгликоля', found: false },
  { fkko_code: '92122001313', name: 'Отходы тормозной жидкости при обслуживании автотранспорта', found: false },
  { fkko_code: '92171131394', name: 'Вода от мойки узлов, деталей автомобильного транспорта, загрязнённая нефтепродуктами (содержание нефтепродуктов менее 15%)', found: false },
];

const CONTRACT_STATUS_MAP: Record<string, { label: string; color: string }> = {
  active: { label: 'Действующий', color: 'success' },
  expired: { label: 'Истёк', color: 'error' },
  none: { label: 'Не привязан', color: 'default' },
};

// Рендер содержимого приложения: строки с «|» (OCR-таблицы) — как HTML-таблица,
// строки без «|» внутри табличного блока — секции на всю ширину, остальное — текст.
const AppendixContent: React.FC<{ content: string }> = ({ content }) => {
  const blocks = useMemo(() => {
    const out: ({ type: 'text'; text: string } | { type: 'table'; rows: string[][] })[] = [];
    let tableRows: string[][] = [];
    const flushTable = () => {
      if (tableRows.length) {
        out.push({ type: 'table', rows: tableRows });
        tableRows = [];
      }
    };
    for (const raw of content.split('\n')) {
      const line = raw.trim();
      if (!line) {
        flushTable();
        continue;
      }
      if (line.includes('|')) {
        const cells = line.split('|').map((c) => c.trim());
        while (cells.length && cells[cells.length - 1] === '') cells.pop();
        tableRows.push(cells);
      } else if (tableRows.length) {
        tableRows.push([line]);
      } else {
        out.push({ type: 'text', text: line });
      }
    }
    flushTable();
    return out;
  }, [content]);

  return (
    <div style={{ fontSize: 13, maxHeight: 400, overflow: 'auto' }}>
      {blocks.map((b, i) => {
        if (b.type === 'text') {
          return <p key={i} style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{b.text}</p>;
        }
        const maxCols = Math.max(...b.rows.map((r) => r.length));
        const [header, ...body] = b.rows;
        return (
          <table key={i} className="appendix-table">
            {header.length > 1 && (
              <thead>
                <tr>
                  {header.map((c, j) => <th key={j}>{c}</th>)}
                </tr>
              </thead>
            )}
            <tbody>
              {(header.length > 1 ? body : b.rows).map((row, ri) => (
                row.length === 1 ? (
                  <tr key={ri}>
                    <td className="appendix-section" colSpan={maxCols}>{row[0]}</td>
                  </tr>
                ) : (
                  <tr key={ri}>
                    {Array.from({ length: maxCols }, (_, j) => <td key={j}>{row[j] || ''}</td>)}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        );
      })}
    </div>
  );
};

const ContractorsTab: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('ecology:write');
  const navigate = useNavigate();

  const [items, setItems] = useState<EcologyContractor[]>([]);
  const [loading, setLoading] = useState(false);

  // Drawer
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [drawerTab, setDrawerTab] = useState('contract');

  // Documents registry in drawer
  const [docs, setDocs] = useState<EcologyContractorDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<EcologyContractorDocument | null>(null);
  const [docForm] = Form.useForm();

  // Appendices in drawer
  const [appendices, setAppendices] = useState<EcologyContractorAppendix[]>([]);
  const [appendicesLoading, setAppendicesLoading] = useState(false);

  // Obligations in drawer
  const [obligations, setObligations] = useState<EcologyObligation[]>([]);
  const [obligationsLoading, setObligationsLoading] = useState(false);
  const [oblModalOpen, setOblModalOpen] = useState(false);
  const [editingObl, setEditingObl] = useState<EcologyObligation | null>(null);
  const [oblForm] = Form.useForm();
  const [reportMonth, setReportMonth] = useState<dayjs.Dayjs>(dayjs().subtract(1, 'month'));
  const [actDate, setActDate] = useState<dayjs.Dayjs>(dayjs());
  const [docxLoading, setDocxLoading] = useState(false);

  // Contractor form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EcologyContractor | null>(null);
  const [form] = Form.useForm();

  // Scanned documents for selectors
  const [scanDocs, setScanDocs] = useState<{ id: number; title: string }[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listContractors());
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить подрядчиков');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDocs = useCallback(async (contractorId: number) => {
    setDocsLoading(true);
    try {
      setDocs(await listContractorDocuments(contractorId));
    } catch {
      message.error('Не удалось загрузить документы');
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const loadAppendices = useCallback(async (contractorId: number) => {
    setAppendicesLoading(true);
    try {
      setAppendices(await listContractorAppendices(contractorId));
    } catch {
      message.error('Не удалось загрузить приложения');
    } finally {
      setAppendicesLoading(false);
    }
  }, []);

  const loadObligations = useCallback(async (contractorId: number) => {
    setObligationsLoading(true);
    try {
      setObligations(await listContractorObligations(contractorId));
    } catch {
      message.error('Не удалось загрузить обязательства');
    } finally {
      setObligationsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get('/documents').then((res) => {
      setScanDocs((res.data || []).map((d: any) => ({ id: d.id, title: d.title })));
    }).catch(() => {});
  }, [load]);

  const drawerData = items.find((x) => x.id === drawerId) || null;

  const openDrawer = (id: number) => {
    setDrawerId(id);
    setDrawerTab('contract');
    loadDocs(id);
    loadAppendices(id);
    loadObligations(id);
  };

  // ── Obligation modal ─────────────────────────────────────────────

  const openOblModal = (record?: EcologyObligation) => {
    setEditingObl(record || null);
    oblForm.resetFields();
    if (record) {
      oblForm.setFieldsValue({ ...record });
    } else {
      oblForm.setFieldsValue({ periodicity: 'monthly', notify_days_before: 5, is_active: true });
    }
    setOblModalOpen(true);
  };

  const saveObligation = async () => {
    if (!drawerId) return;
    try {
      const values = await oblForm.validateFields();
      if (editingObl) {
        await updateObligation(editingObl.id, values);
        message.success('Обязательство обновлено');
      } else {
        await createContractorObligation(drawerId, values);
        message.success('Обязательство добавлено');
      }
      setOblModalOpen(false);
      loadObligations(drawerId);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const removeObligation = async (id: number) => {
    if (!drawerId) return;
    try {
      await deleteObligation(id);
      message.success('Обязательство удалено');
      loadObligations(drawerId);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  const toggleObligation = async (record: EcologyObligation, active: boolean) => {
    try {
      await updateObligation(record.id, { is_active: active });
      if (drawerId) loadObligations(drawerId);
    } catch {
      message.error('Не удалось изменить обязательство');
    }
  };

  const onDownloadReport = async () => {
    if (!drawerId) return;
    setDocxLoading(true);
    try {
      await downloadMonthlyReport(drawerId, reportMonth.year(), reportMonth.month() + 1);
    } catch (e: any) {
      message.error('Не удалось сформировать отчёт');
    } finally {
      setDocxLoading(false);
    }
  };

  const onDownloadAct = async () => {
    if (!drawerId) return;
    setDocxLoading(true);
    try {
      await downloadTransferAct(drawerId, actDate.format('YYYY-MM-DD'));
    } catch (e: any) {
      message.error('Не удалось сформировать акт');
    } finally {
      setDocxLoading(false);
    }
  };

  // ── Contractor modal ────────────────────────────────────────────

  const openModal = (record?: EcologyContractor) => {
    setEditing(record || null);
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        ...record,
        contract_date: record.contract_date ? dayjs(record.contract_date) : null,
        contract_valid_until: record.contract_valid_until ? dayjs(record.contract_valid_until) : null,
      });
    } else {
      form.setFieldsValue({ legal_entity: 'autoregium', is_active: true });
    }
    setModalOpen(true);
  };

  const saveContractor = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        contract_date: values.contract_date?.format('YYYY-MM-DD') || null,
        contract_valid_until: values.contract_valid_until?.format('YYYY-MM-DD') || null,
      };
      if (editing) {
        await updateContractor(editing.id, payload);
        message.success('Подрядчик обновлён');
      } else {
        await createContractor(payload);
        message.success('Подрядчик добавлен');
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const removeContractor = async (id: number) => {
    try {
      await deleteContractor(id);
      message.success('Подрядчик удалён');
      setDrawerId(null);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  // ── Document modal ──────────────────────────────────────────────

  const openDocModal = (record?: EcologyContractorDocument) => {
    setEditingDoc(record || null);
    docForm.resetFields();
    if (record) {
      docForm.setFieldsValue({
        ...record,
        date: record.date ? dayjs(record.date) : null,
        period: record.period_from || record.period_to
          ? [record.period_from ? dayjs(record.period_from) : null, record.period_to ? dayjs(record.period_to) : null]
          : null,
      });
    } else {
      docForm.setFieldsValue({ doc_type: 'act' });
    }
    setDocModalOpen(true);
  };

  const saveDoc = async () => {
    if (!drawerId) return;
    try {
      const values = await docForm.validateFields();
      const payload = {
        doc_type: values.doc_type,
        number: values.number || null,
        date: values.date?.format('YYYY-MM-DD') || null,
        period_from: values.period?.[0]?.format('YYYY-MM-DD') || null,
        period_to: values.period?.[1]?.format('YYYY-MM-DD') || null,
        scanned_document_id: values.scanned_document_id || null,
        notes: values.notes || null,
      };
      if (editingDoc) {
        await updateContractorDocument(editingDoc.id, payload);
        message.success('Документ обновлён');
      } else {
        await createContractorDocument(drawerId, payload);
        message.success('Документ добавлен');
      }
      setDocModalOpen(false);
      loadDocs(drawerId);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const removeDoc = async (id: number) => {
    if (!drawerId) return;
    try {
      await deleteContractorDocument(id);
      message.success('Документ удалён');
      loadDocs(drawerId);
      load();
    } catch {
      message.error('Не удалось удалить');
    }
  };

  // ── Columns ─────────────────────────────────────────────────────

  const columns = useMemo(() => [
    {
      title: 'Подрядчик',
      dataIndex: 'name',
      render: (v: string, r: EcologyContractor) => (
        <div>
          <div>{v}</div>
          {r.inn && <Text type="secondary" style={{ fontSize: 12 }}>ИНН {r.inn}</Text>}
        </div>
      ),
    },
    {
      title: 'Договор',
      render: (_: unknown, r: EcologyContractor) => (
        r.contract_number
          ? `№ ${r.contract_number}${r.contract_date ? ` от ${dayjs(r.contract_date).format('DD.MM.YYYY')}` : ''}`
          : '—'
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'contract_status',
      width: 130,
      render: (v: string) => {
        const s = CONTRACT_STATUS_MAP[v] || { label: v, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    { title: 'Документов', dataIndex: 'documents_count', width: 110 },
    ...(canWrite ? [{
      title: '',
      width: 90,
      render: (_: unknown, r: EcologyContractor) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button icon={<EditOutlined />} size="small" onClick={() => openModal(r)} />
          <Popconfirm title="Удалить подрядчика?" onConfirm={() => removeContractor(r.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ], [canWrite]);

  const docColumns = useMemo(() => [
    {
      title: 'Тип',
      dataIndex: 'doc_type',
      width: 90,
      render: (v: ContractorDocType) => CONTRACTOR_DOC_TYPE_LABELS[v] || v,
    },
    { title: 'Номер', dataIndex: 'number', render: (v?: string | null) => v || '—' },
    {
      title: 'Дата',
      dataIndex: 'date',
      width: 110,
      render: (v?: string | null) => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
    },
    {
      title: 'Период',
      width: 190,
      render: (_: unknown, r: EcologyContractorDocument) => (
        r.period_from || r.period_to
          ? `${r.period_from ? dayjs(r.period_from).format('DD.MM.YYYY') : '…'} — ${r.period_to ? dayjs(r.period_to).format('DD.MM.YYYY') : '…'}`
          : '—'
      ),
    },
    {
      title: 'Скан',
      dataIndex: 'scanned_document_id',
      width: 80,
      render: (v?: number | null) => (
        v ? <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/documents/${v}`)} /> : '—'
      ),
    },
    ...(canWrite ? [{
      title: '',
      width: 90,
      render: (_: unknown, r: EcologyContractorDocument) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openDocModal(r)} />
          <Popconfirm title="Удалить документ?" onConfirm={() => removeDoc(r.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ], [canWrite, navigate]);

  const fkkoColumns = useMemo(() => [
    { title: 'Код ФККО', dataIndex: 'fkko_code', width: 140 },
    { title: 'Наименование отхода', dataIndex: 'name' },
    {
      title: 'В выписке',
      dataIndex: 'found',
      width: 110,
      render: (v: boolean) => (v ? <Tag color="success">Найден</Tag> : <Tag color="error">Не найден</Tag>),
    },
  ], []);

  const isSitimatic = !!drawerData && /ситиматик/i.test(drawerData.name);

  return (
    <>
      {canWrite && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Добавить подрядчика
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        onRow={(record) => ({ onClick: () => openDrawer(record.id), style: { cursor: 'pointer' } })}
      />

      <Drawer
        open={!!drawerData}
        onClose={() => setDrawerId(null)}
        width={760}
        title={drawerData?.name || 'Подрядчик'}
      >
        {drawerData && (
          <Tabs activeKey={drawerTab} onChange={setDrawerTab}>
            <TabPane tab="Договор" key="contract">
              {drawerData.contract_document_id ? (
                <>
                  <p>
                    <Text strong>№ {drawerData.contract_number || '—'}</Text>
                    {drawerData.contract_date && (
                      <Text> от {dayjs(drawerData.contract_date).format('DD.MM.YYYY')}</Text>
                    )}
                  </p>
                  {drawerData.contract_valid_until && (
                    <p>Действует до: {dayjs(drawerData.contract_valid_until).format('DD.MM.YYYY')}</p>
                  )}
                  <Tag color={CONTRACT_STATUS_MAP[drawerData.contract_status]?.color}>
                    {CONTRACT_STATUS_MAP[drawerData.contract_status]?.label}
                  </Tag>
                  <div style={{ marginTop: 16 }}>
                    <Button
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => navigate(`/documents/${drawerData.contract_document_id}`)}
                    >
                      Открыть текст и скан
                    </Button>
                  </div>
                </>
              ) : (
                <Text type="secondary">
                  Договор не привязан. {canWrite && 'Нажмите «Изменить» на вкладке «Информация».'}
                </Text>
              )}
            </TabPane>

            <TabPane tab={`Акты и отчёты (${docs.length})`} key="docs">
              {canWrite && (
                <Button icon={<PlusOutlined />} onClick={() => openDocModal()} style={{ marginBottom: 12 }}>
                  Добавить документ
                </Button>
              )}
              <Table columns={docColumns} dataSource={docs} rowKey="id" loading={docsLoading} pagination={false} size="small" />
            </TabPane>

            <TabPane tab={`Приложения (${appendices.length})`} key="appendices">
              {appendicesLoading ? null : appendices.length === 0 ? (
                <Text type="secondary">Приложения к договору не добавлены.</Text>
              ) : (
                <Collapse
                  items={appendices.map((a) => ({
                    key: a.id,
                    label: (
                      <Space>
                        <Text strong>№ {a.appendix_number}</Text>
                        <span>{a.title}</span>
                        <Tag>{APPENDIX_KIND_LABELS[a.kind] || a.kind}</Tag>
                      </Space>
                    ),
                    children: <AppendixContent content={a.content || '—'} />,
                  }))}
                />
              )}
            </TabPane>

            <TabPane tab={`Обязательства (${obligations.length})`} key="obligations">
              <Space style={{ marginBottom: 12 }} wrap>
                {canWrite && (
                  <Button icon={<PlusOutlined />} onClick={() => openOblModal()}>
                    Добавить обязательство
                  </Button>
                )}
                <Space.Compact>
                  <DatePicker
                    picker="month"
                    value={reportMonth}
                    onChange={(v) => v && setReportMonth(v)}
                    allowClear={false}
                  />
                  <Button icon={<DownloadOutlined />} loading={docxLoading} onClick={onDownloadReport}>
                    Отчёт DOCX
                  </Button>
                </Space.Compact>
                <Space.Compact>
                  <DatePicker
                    value={actDate}
                    onChange={(v) => v && setActDate(v)}
                    allowClear={false}
                    format="DD.MM.YYYY"
                  />
                  <Button icon={<DownloadOutlined />} loading={docxLoading} onClick={onDownloadAct}>
                    Акт приёма-передачи DOCX
                  </Button>
                </Space.Compact>
              </Space>
              <Table
                rowKey="id"
                loading={obligationsLoading}
                pagination={false}
                size="small"
                dataSource={obligations}
                columns={[
                  {
                    title: 'Обязательство',
                    key: 'title',
                    render: (_, r) => (
                      <Space direction="vertical" size={0}>
                        <Space>
                          <Text strong={r.is_active}>{r.title}</Text>
                          {r.contract_clause && <Tag>п. {r.contract_clause}</Tag>}
                          {!r.is_active && <Tag>неактивно</Tag>}
                        </Space>
                        {r.recipient && <Text type="secondary" style={{ fontSize: 12 }}>Кому: {r.recipient}</Text>}
                      </Space>
                    ),
                  },
                  {
                    title: 'Срок',
                    key: 'due',
                    width: 210,
                    render: (_, r) => (
                      <Space direction="vertical" size={0}>
                        <span>{r.due_day ? `до ${r.due_day} числа ежемесячно` : '—'}</span>
                        {r.next_due_date && (
                          <Text
                            type={r.days_left !== null && r.days_left !== undefined && r.days_left < 0 ? 'danger' : 'secondary'}
                            style={{ fontSize: 12 }}
                          >
                            ближайший: {dayjs(r.next_due_date).format('DD.MM.YYYY')}
                            {r.days_left !== null && r.days_left !== undefined && (
                              r.days_left < 0
                                ? ` (просрочено ${-r.days_left} дн.)`
                                : ` (через ${r.days_left} дн.)`
                            )}
                          </Text>
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: 'Напомнить',
                    key: 'notify',
                    width: 130,
                    render: (_, r) => `за ${r.notify_days_before} дн.`,
                  },
                  ...(canWrite ? [{
                    title: '',
                    key: 'actions',
                    width: 130,
                    render: (_: unknown, r: EcologyObligation) => (
                      <Space>
                        <Switch
                          size="small"
                          checked={r.is_active}
                          onChange={(v) => toggleObligation(r, v)}
                          title="Активно"
                        />
                        <Button size="small" icon={<EditOutlined />} onClick={() => openOblModal(r)} />
                        <Popconfirm title="Удалить обязательство?" onConfirm={() => removeObligation(r.id)}>
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    ),
                  }] : []),
                ]}
              />
            </TabPane>

            {isSitimatic && (
              <TabPane tab="Сверка ФККО" key="fkko">
                <Text type="secondary">
                  Сравнение кодов ФККО из паспортов отходов с выпиской из реестра лицензий № 194987 (Ситиматик).
                  Всего: {SITIMATIC_FKKO.length}. Найдено: {SITIMATIC_FKKO.filter((d) => d.found).length}.
                </Text>
                <Table
                  columns={fkkoColumns}
                  dataSource={SITIMATIC_FKKO}
                  rowKey="fkko_code"
                  pagination={false}
                  size="small"
                  style={{ marginTop: 12 }}
                  rowClassName={(record) => (record.found ? '' : 'ecology-fkko-missing')}
                />
              </TabPane>
            )}

            <TabPane tab="Информация" key="info">
              <p><Text type="secondary">Юрлицо: </Text>{LEGAL_ENTITY_LABELS[drawerData.legal_entity] || drawerData.legal_entity}</p>
              {drawerData.inn && <p><Text type="secondary">ИНН: </Text>{drawerData.inn}</p>}
              {drawerData.contact_info && <p><Text type="secondary">Контакты: </Text>{drawerData.contact_info}</p>}
              {drawerData.notes && <p><Text type="secondary">Заметки: </Text>{drawerData.notes}</p>}
              <p><Text type="secondary">Статус: </Text>{drawerData.is_active ? 'Активен' : 'Архив'}</p>
              {canWrite && (
                <Button icon={<EditOutlined />} onClick={() => openModal(drawerData)}>Изменить</Button>
              )}
            </TabPane>
          </Tabs>
        )}
      </Drawer>

      {/* Contractor modal */}
      <Modal
        open={modalOpen}
        title={editing ? 'Редактировать подрядчика' : 'Новый подрядчик'}
        onOk={saveContractor}
        onCancel={() => setModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Укажите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="inn" label="ИНН"><Input /></Form.Item>
          <Form.Item name="legal_entity" label="Наше юрлицо">
            <Select options={[
              { value: 'autoregium', label: LEGAL_ENTITY_LABELS.autoregium },
              { value: 'fomin', label: LEGAL_ENTITY_LABELS.fomin },
            ]} />
          </Form.Item>
          <Form.Item name="contract_document_id" label="Скан договора">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={scanDocs.map((d) => ({ value: d.id, label: d.title }))}
            />
          </Form.Item>
          <Form.Item name="contract_number" label="Номер договора"><Input /></Form.Item>
          <Form.Item name="contract_date" label="Дата договора"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="contract_valid_until" label="Действует до (пусто — бессрочный)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="contact_info" label="Контакты"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="notes" label="Заметки"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="is_active" label="Активен" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      {/* Document modal */}
      <Modal
        open={docModalOpen}
        title={editingDoc ? 'Редактировать документ' : 'Новый документ'}
        onOk={saveDoc}
        onCancel={() => setDocModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        width={560}
      >
        <Form form={docForm} layout="vertical">
          <Form.Item name="doc_type" label="Тип" rules={[{ required: true }]}>
            <Select options={Object.entries(CONTRACTOR_DOC_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
          </Form.Item>
          <Form.Item name="number" label="Номер"><Input /></Form.Item>
          <Form.Item name="date" label="Дата"><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="period" label="Период (за какой срок документ)">
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="scanned_document_id" label="Скан">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={scanDocs.map((d) => ({ value: d.id, label: d.title }))}
            />
          </Form.Item>
          <Form.Item name="notes" label="Заметки"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Obligation modal */}
      <Modal
        open={oblModalOpen}
        title={editingObl ? 'Редактировать обязательство' : 'Новое обязательство'}
        onOk={saveObligation}
        onCancel={() => setOblModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        width={560}
      >
        <Form form={oblForm} layout="vertical">
          <Form.Item name="title" label="Название" rules={[{ required: true, message: 'Укажите название' }]}>
            <Input placeholder="Ежемесячный отчёт по переданным на размещение отходам" />
          </Form.Item>
          <Form.Item name="contract_clause" label="Пункт договора">
            <Input placeholder="3.1.14" />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="periodicity" label="Периодичность" rules={[{ required: true }]}>
            <Select options={[{ value: 'monthly', label: 'Ежемесячно' }]} />
          </Form.Item>
          <Form.Item name="due_day" label="Срок — до какого числа месяца" rules={[{ required: true, message: 'Укажите число' }]}>
            <InputNumber min={1} max={31} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notify_days_before" label="Напоминать за (дней)">
            <InputNumber min={0} max={31} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="recipient" label="Кому направлять">
            <Input placeholder="murmansk@citymatic.ru" />
          </Form.Item>
          <Form.Item name="appendix_number" label="Номер приложения (форма)">
            <InputNumber min={1} max={99} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Активно" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ContractorsTab;
